
import { useState } from 'react';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false); // Close menu after navigation
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img 
              src="https://public.readdy.ai/ai/img_res/95e9c46e-5455-4a4a-a610-9561bd551b4a.png" 
              alt="Leachianus Logo" 
              className="h-10 w-10 md:h-12 md:w-12 object-contain"
            />
            <span className={`text-lg md:text-xl font-semibold tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              LEACHIANUS
            </span>
          </div>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('home')}
              className={`text-sm font-medium transition-all hover:opacity-100 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-all hover:opacity-100 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('habitat')}
              className={`text-sm font-medium transition-all hover:opacity-100 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Habitat
            </button>
            <button
              onClick={() => scrollToSection('care')}
              className={`text-sm font-medium transition-all hover:opacity-100 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Care Guide
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className={`text-sm font-medium transition-all hover:opacity-100 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-all hover:opacity-100 whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'
              }`}
            >
              Contact
            </button>
          </div>

          {/* Desktop CTA Button - Hidden on mobile */}
          <button className="hidden lg:flex bg-[#B8E986] text-gray-900 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#a8d976] transition-colors items-center gap-2 whitespace-nowrap cursor-pointer">
            Get Care Sheet
            <i className="ri-arrow-right-line"></i>
          </button>

          {/* Mobile Hamburger Menu Button */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`text-2xl ${scrolled ? 'text-gray-900' : 'text-white'} ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className={`lg:hidden mt-4 pb-4 ${scrolled ? 'bg-white' : 'bg-black/20 backdrop-blur-sm'} rounded-lg`}>
            <div className="flex flex-col gap-4 px-4">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-left py-2 text-base font-medium transition-all whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-left py-2 text-base font-medium transition-all whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('habitat')}
                className={`text-left py-2 text-base font-medium transition-all whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Habitat
              </button>
              <button
                onClick={() => scrollToSection('care')}
                className={`text-left py-2 text-base font-medium transition-all whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Care Guide
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className={`text-left py-2 text-base font-medium transition-all whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-left py-2 text-base font-medium transition-all whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
              >
                Contact
              </button>
              
              {/* Mobile CTA Button */}
              <button className="bg-[#B8E986] text-gray-900 px-6 py-3 rounded-full text-base font-medium hover:bg-[#a8d976] transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer mt-2">
                Get Care Sheet
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
